import React, { useState } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Table, Typography, Space, theme, Button, Modal, Form, Input, Select, InputNumber, message, Popconfirm } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DollarOutlined, LineChartOutlined, PlusOutlined, DeleteOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { addFinance } from '../../store/slices/financeSlice';
import { useFinance } from '../../hooks/useFinance';

// Finance Category enum mapping
const financeCategories = [
  { value: 0, label: 'Paket Satışı' },
  { value: 1, label: 'Eğitmen Ödemesi' },
  { value: 2, label: 'Faturalar' },
  { value: 3, label: 'Ekipman' },
  { value: 4, label: 'Diğer' }
];

const Account = () => {
  const { token } = theme.useToken();
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showFinancialValues, setShowFinancialValues] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  // Finance hook
  const { financialData, loading, refetchFinance } = useFinance(dateRange[0], dateRange[1]);



  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      sorter: (a, b) => new Date(a.transactionDate) - new Date(b.transactionDate),
      render: (text) => dayjs(text).format('DD.MM.YYYY'),
      responsive: ['md']
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      responsive: ['sm'],
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: 'Kategori',
      dataIndex: 'financeCategory',
      key: 'financeCategory',
      filters: financeCategories.map(category => ({ text: category.label, value: category.value })),
      onFilter: (value, record) => record.financeCategory === value,
      render: (text) => {
        const category = financeCategories.find(cat => cat.value === text);
        return category ? category.label : 'Bilinmiyor';
      },
      responsive: ['lg']
    },
    {
      title: 'Tür',
      dataIndex: 'financeType',
      key: 'financeType',
      filters: [
        { text: 'Gelir', value: 0 },
        { text: 'Gider', value: 1 }
      ],
      onFilter: (value, record) => record.financeType === value,
      render: (text) => (
        <span style={{ color: text === 0 ? token.colorSuccess : token.colorError }}>
          {text === 0 ? 'Gelir' : 'Gider'}
        </span>
      ),
      responsive: ['md']
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (text, record) => (
        <span style={{ color: record.financeType === 0 ? token.colorSuccess : token.colorError }}>
          {showFinancialValues ? record.financeType === 0 ? '+' + text.toLocaleString('tr-TR') : '-' + text.toLocaleString('tr-TR') + ' ₺' : '***'}
        </span>
      )
    },
  ];

  const handleAddTransaction = (values) => {
    const transactionData = {
      description: values.description,
      transactionDate: values.date.format('YYYY-MM-DD'),
      financeType: values.type === 'income' ? 0 : 1,
      financeCategory: values.category,
      amount: values.amount
    };

    dispatch(addFinance(transactionData))
      .unwrap()
      .then((res) => {
        if (res?.id) {
          message.success('İşlem başarıyla eklendi');
          // Refresh the financial data
          refetchFinance();
        } else {
          message.error('İşlem eklenirken hata oluştu');
        }
      })
      .catch(() => {
        message.error('İşlem eklenirken hata oluştu');
      });

    setIsModalVisible(false);
    form.resetFields();
  };



  // Finansal değerleri formatla (maskeleme ile)
  const formatFinancialValue = (value) => {
    if (showFinancialValues) {
      return value;
    }
    return '***';
  };

  return (
    <div style={{ padding: '16px', maxWidth: '100%', overflowX: 'auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header Section */}
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col xs={24} sm={12} md={8}>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              format="DD/MM/YYYY"
              placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.setFieldsValue({
                  date: dayjs()
                })
                setIsModalVisible(true)
              }}
            >
              Yeni İşlem Ekle
            </Button>
          </Col>
        </Row>

        {/* Hızlı Tarih Seçimi */}
        <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
          <Col>
            <Typography.Text strong style={{ marginRight: 8 }}>Hızlı Seçim:</Typography.Text>
          </Col>
          <Col>
            <Space size="small" wrap>
              <Button 
                size="small" 
                onClick={() => setDateRange([dayjs().startOf('month'), dayjs().endOf('month')])}
                type={dateRange[0]?.isSame(dayjs().startOf('month')) ? 'primary' : 'default'}
              >
                Bu Ay
              </Button>
              <Button 
                size="small" 
                onClick={() => setDateRange([dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')])}
                type={dateRange[0]?.isSame(dayjs().subtract(1, 'month').startOf('month')) ? 'primary' : 'default'}
              >
                Geçen Ay
              </Button>
              <Button 
                size="small" 
                onClick={() => setDateRange([dayjs().subtract(3, 'month').startOf('month'), dayjs().endOf('month')])}
                type={dateRange[0]?.isSame(dayjs().subtract(3, 'month').startOf('month')) ? 'primary' : 'default'}
              >
                Son 3 Ay
              </Button>
              <Button 
                size="small" 
                onClick={() => setDateRange([dayjs().subtract(1, 'year').startOf('month'), dayjs().endOf('month')])}
                type={dateRange[0]?.isSame(dayjs().subtract(1, 'year').startOf('month')) ? 'primary' : 'default'}
              >
                Son 1 Yıl
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Financial Values Toggle */}
        <Row justify="end" style={{ marginBottom: '16px' }}>
          <Col>
            <Typography.Link
              onClick={() => setShowFinancialValues(!showFinancialValues)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '16px',
                color: token.colorPrimary
              }}
            >
              {showFinancialValues ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              {showFinancialValues ? 'Finansal Değerleri Gizle' : 'Finansal Değerleri Göster'}
            </Typography.Link>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Card loading={loading} style={{ height: '100%' }}>
              <Statistic
                title="Toplam Gelir"
                value={formatFinancialValue(financialData?.totalIncome || 0)}
                precision={0}
                valueStyle={{ color: token.colorSuccess, fontSize: { xs: '16px', sm: '20px', md: '24px' } }}
                prefix={<ArrowUpOutlined />}
                suffix="₺"
                loading={loading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card loading={loading} style={{ height: '100%' }}>
              <Statistic
                title="Toplam Gider"
                value={formatFinancialValue(financialData?.totalExpense || 0)}
                precision={0}
                valueStyle={{ color: token.colorError, fontSize: { xs: '16px', sm: '20px', md: '24px' } }}
                prefix={<ArrowDownOutlined />}
                suffix="₺"
                loading={loading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} lg={8}>
            <Card loading={loading} style={{ height: '100%' }}>
              <Statistic
                title="Net Kar"
                value={formatFinancialValue(financialData?.totalProfit || 0)}
                precision={0}
                valueStyle={{ color: financialData?.totalProfit >= 0 ? token.colorSuccess : token.colorError, fontSize: { xs: '16px', sm: '20px', md: '24px' } }}
                prefix={<DollarOutlined />}
                suffix="₺"
                loading={loading}
              />
            </Card>
          </Col>
        </Row>

        {/* Transactions Table */}
        <Card
          title="İşlem Geçmişi"
          extra={<LineChartOutlined style={{ fontSize: '20px', color: token.colorPrimary }} />}
          style={{ overflowX: 'auto' }}
        >
          <Table
            columns={columns}
            dataSource={financialData?.transactions || []}
            loading={loading}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
              responsive: true
            }}
            rowKey="id"
            scroll={{ x: 800 }}
            size={{ xs: 'small', sm: 'default' }}
          />
        </Card>

        {/* Add Transaction Modal */}
        <Modal
          title="Yeni İşlem Ekle"
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
          }}
          footer={null}
          destroyOnClose={true}
          width={{ xs: '95%', sm: '80%', md: '600px' }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddTransaction}
          >
            <Form.Item
              name="date"
              label="Tarih"
              rules={[{ required: true, message: 'Lütfen tarih seçin' }]}
            >
              <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="description"
              label="Açıklama"
              rules={[{ required: true, message: 'Lütfen açıklama girin' }]}
            >
              <Input placeholder="İşlem açıklaması" />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="type"
                  label="İşlem Türü"
                  rules={[{ required: true, message: 'Lütfen işlem türü seçin' }]}
                >
                  <Select placeholder="İşlem türü seçin">
                    <Select.Option value="income">Gelir</Select.Option>
                    <Select.Option value="expense">Gider</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="category"
                  label="Kategori"
                  rules={[{ required: true, message: 'Lütfen kategori seçin' }]}
                >
                  <Select placeholder="Kategori seçin">
                    {financeCategories.map(category => (
                      <Select.Option key={category.value} value={category.value}>
                        {category.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="amount"
              label="Tutar"
              rules={[{ required: true, message: 'Lütfen tutar girin' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                min={0}
                placeholder="0"
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Kaydet
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </Space>
    </div>
  );
};

export default Account;